import { ref, readonly } from 'vue';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';

/**
 * Whether the dark theme is applied to the document right now. This is the
 * resolved state, not the stored preference: `auto` follows the OS, and the
 * `dark_mode` feature flag can force light regardless of what is stored.
 * @type {import('vue').Ref<boolean>}
 */
const isDarkThemeActive = ref(
  document.body?.classList.contains('dark') ?? false
);

/**
 * Read-only handle on the resolved theme, for components that have to adapt
 * markup we do not control (email HTML, for one) rather than styling it.
 * @returns {import('vue').Ref<boolean>} Reactive, true while dark is applied
 */
export const useIsDarkThemeActive = () => readonly(isDarkThemeActive);

const applyTheme = isDark => {
  document.body.classList.toggle('dark', isDark);
  document.documentElement.style.setProperty(
    'color-scheme',
    isDark ? 'dark' : 'light'
  );
  isDarkThemeActive.value = isDark;
};

export const setColorTheme = isOSOnDarkMode => {
  const selectedColorScheme =
    LocalStorage.get(LOCAL_STORAGE_KEYS.COLOR_SCHEME) || 'light';
  applyTheme(
    (selectedColorScheme === 'auto' && isOSOnDarkMode) ||
      selectedColorScheme === 'dark'
  );
};

/**
 * Reset the document to the light theme, ignoring any stored preference.
 * Needed when the feature flag is off but the user had picked dark earlier.
 * @returns {void}
 */
export const forceLightTheme = () => applyTheme(false);
